/**
 * Live presence in a project: one websocket per open project to vfs-service's presence room
 * (`/ws/v1/projects/:id/presence`, vfs-service src/collab/presence.js has the protocol). In per-person mode the
 * sandbox gateway forwards it to the project owner's pod, like the doc and tree sockets.
 *
 * Shared by every component of the page that shows presence for the same project (header avatars, file tree, kanban,
 * editor): `acquirePresence` opens it (counted, closed with the last release), `subscribePresence` /
 * `getPresenceSnapshot` feed `useSyncExternalStore` (hooks/useProjectPresence.js), and the setters publish where this
 * tab is (`setPresenceView`, one view per source: the editor's file wins over the page's service or Home) and its
 * cursor (`setPresenceCursor`). Privacy (`collab.presence`, `collab.hideCursor`, `collab.followable`, `collab.typingOnly`) comes from the person's settings and
 * is sent on every socket; `setPresencePrivacy` applies a change at once.
 */
import config from '../config';
import { getSettings } from '../services/SettingsService';

const PING_MS = 15000;
const MIN_RETRY_MS = 1000;
const MAX_RETRY_MS = 15000;
// The view that says the most wins: a file in the editor, then a chat, a service, the kanban, Home.
const VIEW_RANK = { file: 5, chat: 4, preview: 3, service: 2, kanban: 1, home: 0 };

const EMPTY = Object.freeze({ status: 'off', you: null, people: [], agents: [], conflicts: [], previews: {}, refused: null, following: null, error: null, notesAt: null });
// The pointer over this tab's preview is sent at most this often (vfs-service keeps one every 50 ms at most).
const POINTER_MS = 100;

const channels = new Map();
// projectId -> the person's name for it, kept past a channel's last release (a reopened channel says it again).
const names = new Map();
let privacy = { presence: true, hideCursor: false, followable: true, typingOnly: false };
let privacyLoaded = null;

/** The presence socket's URL, or null when vfs-service is not configured. Exported for tests. */
export function presenceUrl(projectId, base = config.vfsServiceUrl) {
  if (!base || !projectId) { return null; }
  const absolute = base.startsWith('/') && typeof window !== 'undefined' ? `${window.location.origin}${base}` : base;
  return `${absolute.replace(/\/+$/, '').replace(/^http/i, 'ws')}/ws/v1/projects/${encodeURIComponent(projectId)}/presence`;
}

function loadPrivacy() {
  if (!privacyLoaded) {
    privacyLoaded = getSettings()
      .then((settings) => {
        if (settings?.collab) { setPresencePrivacy(settings.collab); }
      })
      .catch(() => {});
  }
  return privacyLoaded;
}

function update(channel, patch) {
  channel.snapshot = { ...channel.snapshot, ...patch };
  channel.listeners.forEach((listener) => {
    try { listener(); } catch { /* a listener never breaks the others */ }
  });
}

function send(channel, message) {
  const { ws } = channel;
  if (ws && ws.readyState === 1) {
    ws.send(JSON.stringify(message));
  }
}

function effectiveView(channel) {
  let best = null;
  channel.views.forEach((view) => {
    if (view && (!best || (VIEW_RANK[view.kind] ?? 0) > (VIEW_RANK[best.kind] ?? 0))) { best = view; }
  });
  return best || { kind: 'home' };
}

function connect(channel) {
  const url = presenceUrl(channel.projectId);
  if (!url || typeof WebSocket === 'undefined') {
    update(channel, { status: 'off' });
    return;
  }
  const ws = new WebSocket(url);
  channel.ws = ws;
  update(channel, { status: 'connecting' });
  ws.onopen = () => {
    channel.retryMs = MIN_RETRY_MS;
    update(channel, { status: 'open' });
    send(channel, { type: 'hello', name: names.get(channel.projectId) || undefined, view: effectiveView(channel), privacy });
    if (channel.cursor && !privacy.hideCursor) { send(channel, { type: 'cursor', cursor: channel.cursor }); }
    if (channel.following) { send(channel, { type: 'follow', target: channel.following }); }
    clearInterval(channel.ping);
    channel.ping = setInterval(() => send(channel, { type: 'ping' }), PING_MS);
  };
  ws.onmessage = (event) => {
    let message;
    try { message = JSON.parse(event.data); } catch { return; }
    if (message.type === 'welcome') { update(channel, { you: message.you }); }
    if (message.type === 'presence') { update(channel, { people: message.people || [], agents: message.agents || [], conflicts: message.conflicts || [], previews: message.previews || {} }); }
    if (message.type === 'refused') { update(channel, { refused: { path: message.path, message: message.message, at: Date.now() } }); }
    if (message.type === 'error') { update(channel, { error: { message: message.message || 'Not allowed', at: Date.now() } }); }
    // The project's notes changed (a message sent, edited or deleted): an open Messages reloads (collab/Messages.jsx).
    if (message.type === 'notes') { update(channel, { notesAt: message.at || new Date().toISOString() }); }
  };
  ws.onclose = () => {
    clearInterval(channel.ping);
    if (channel.ws !== ws) { return; }
    channel.ws = null;
    if (channel.refs <= 0) { return; }
    update(channel, { status: 'connecting', people: [], agents: [], conflicts: [] });
    channel.retry = setTimeout(() => connect(channel), channel.retryMs);
    channel.retryMs = Math.min(channel.retryMs * 2, MAX_RETRY_MS);
  };
  ws.onerror = () => {};
}

function channelOf(projectId) {
  let channel = channels.get(projectId);
  if (!channel) {
    channel = { projectId, ws: null, refs: 0, listeners: new Set(), snapshot: EMPTY, views: new Map(), cursor: null, retryMs: MIN_RETRY_MS, following: null, followPath: null };
    channels.set(projectId, channel);
  }
  return channel;
}

/** The signed-in person's name, sent in the hello (vfs-service prefers the name the project has for them). */
export function setPresenceName(projectId, name) {
  if (!projectId || !name) { return; }
  if (names.get(projectId) === name) { return; }
  names.set(projectId, name);
  // Already open without it: say it now.
  const channel = channels.get(projectId);
  if (channel) { send(channel, { type: 'hello', name, view: effectiveView(channel), privacy }); }
}

/** Opens (or shares) the project's presence socket; call the returned function to let go of it. */
export function acquirePresence(projectId) {
  if (!projectId) { return () => {}; }
  const channel = channelOf(projectId);
  channel.refs += 1;
  if (channel.refs === 1) {
    loadPrivacy();
    connect(channel);
  }
  let released = false;
  return () => {
    if (released) { return; }
    released = true;
    channel.refs -= 1;
    if (channel.refs > 0) { return; }
    clearTimeout(channel.retry);
    clearInterval(channel.ping);
    clearTimeout(channel.pointerTimer);
    const { ws } = channel;
    channel.ws = null;
    if (ws) { try { ws.close(); } catch { /* already closed */ } }
    channels.delete(projectId);
  };
}

export function subscribePresence(projectId, listener) {
  const channel = channelOf(projectId);
  channel.listeners.add(listener);
  return () => {
    channel.listeners.delete(listener);
    if (channel.refs <= 0 && channel.listeners.size === 0) { channels.delete(projectId); }
  };
}

export function getPresenceSnapshot(projectId) {
  return channels.get(projectId)?.snapshot || EMPTY;
}

/** A file path as presence names it: no leading `./` (the editor keeps `./brain/docs/x.md` for files outside services/). */
export const presencePath = (path) => String(path || '').replace(/^(\.\/)+/, '');

/** Where this tab is, for one `source` (e.g. 'editor', 'page'); null clears that source's view. */
export function setPresenceView(projectId, source, rawView) {
  if (!projectId) { return; }
  const view = rawView && rawView.path ? { ...rawView, path: presencePath(rawView.path) } : rawView;
  // Kept even before the socket is opened (a child's effect runs before its parent's): the hello carries it.
  const channel = channelOf(projectId);
  const before = JSON.stringify(effectiveView(channel));
  // Following someone stops when you open another file yourself (follow mode opens theirs: `expectFollowPath`).
  if (channel.following && source === 'editor' && view?.path && channel.followPath && view.path !== channel.followPath) {
    followPerson(projectId, null);
  }
  if (view) { channel.views.set(source, view); } else { channel.views.delete(source); }
  const after = effectiveView(channel);
  if (JSON.stringify(after) !== before) { send(channel, { type: 'view', view: after }); }
}

/** This tab's cursor in a file (`{ path, line, column, selection?, typing? }`), or null. Not sent with hide my cursor. */
export function setPresenceCursor(projectId, cursor) {
  if (!projectId) { return; }
  const channel = channelOf(projectId);
  channel.cursor = cursor;
  if (!privacy.hideCursor || !cursor) { send(channel, { type: 'cursor', cursor }); }
}

/**
 * Where this tab is in a file now, `{ path, line }` (line: the cursor's, or null), or null when no file is open: "about
 * this file" for a message (components/collab/Messages.jsx). Local only, so it works with hide my cursor too.
 */
export function getOwnPlace(projectId) {
  const channel = projectId ? channels.get(projectId) : null;
  if (!channel) { return null; }
  const view = effectiveView(channel);
  const path = view?.kind === 'file' && view.path ? view.path : null;
  if (!path) { return null; }
  const cursor = channel.cursor && presencePath(channel.cursor.path) === path ? channel.cursor : null;
  return { path, line: Number.isInteger(cursor?.line) ? cursor.line : null };
}

/**
 * This tab's pointer over its preview (`{ x, y }`, fractions of the preview's width and height), or null when it left
 * it: throttled to one every POINTER_MS, the last one always sent. Never sent with hide my cursor.
 */
export function setPresencePointer(projectId, pointer) {
  if (!projectId) { return; }
  const channel = channelOf(projectId);
  const next = pointer && Number.isFinite(pointer.x) && Number.isFinite(pointer.y)
    ? { x: Math.min(1, Math.max(0, pointer.x)), y: Math.min(1, Math.max(0, pointer.y)) }
    : null;
  channel.pendingPointer = next;
  if (privacy.hideCursor) { return; }
  const flush = () => {
    channel.pointerTimer = null;
    channel.pointerSentAt = Date.now();
    send(channel, { type: 'pointer', pointer: channel.pendingPointer });
  };
  if (channel.pointerTimer) { return; }
  const wait = POINTER_MS - (Date.now() - (channel.pointerSentAt || 0));
  if (wait <= 0) { flush(); } else { channel.pointerTimer = setTimeout(flush, wait); }
}

/** The person's privacy choices (settings `collab`), applied to every open socket. */
export function setPresencePrivacy(next) {
  const pick = (key) => (typeof next?.[key] === 'boolean' ? next[key] : privacy[key]);
  privacy = { presence: pick('presence'), hideCursor: pick('hideCursor'), followable: pick('followable'), typingOnly: pick('typingOnly') };
  channels.forEach((channel) => {
    send(channel, { type: 'privacy', privacy });
    if (privacy.hideCursor) { send(channel, { type: 'cursor', cursor: null }); }
  });
}

export const getPresencePrivacy = () => privacy;

/**
 * Follow a person (their id) in the project, or stop (null): the page opens the file they are in and the editor scrolls
 * with their cursor (components/collab/FollowController.jsx, MonacoFileEditor). Others see that you follow them.
 */
export function followPerson(projectId, personId) {
  if (!projectId) { return; }
  const channel = channelOf(projectId);
  const target = personId || null;
  if (channel.following === target) { return; }
  channel.following = target;
  channel.followPath = null;
  update(channel, { following: target });
  send(channel, { type: 'follow', target });
}

/** Follow mode is about to open `path` (the followed person's file): opening it does not stop following. */
export function expectFollowPath(projectId, path) {
  const channel = channels.get(projectId);
  if (channel) { channel.followPath = path ? presencePath(path) : null; }
}

/** Pause an AI agent working in the project (owner: any; editor: the ones they drive), or resume it. */
export function pauseAgent(projectId, agentId, paused = true) {
  if (!projectId || !agentId) { return; }
  const channel = channelOf(projectId);
  update(channel, { error: null });
  send(channel, { type: paused ? 'pause' : 'resume', agentId });
}

/** Forget the last error the presence server answered (shown once). */
export function clearPresenceError(projectId) {
  const channel = channels.get(projectId);
  if (channel && channel.snapshot.error) { update(channel, { error: null }); }
}

/** For tests: forget every channel and the loaded privacy. */
export function resetPresenceForTests() {
  channels.forEach((channel) => {
    clearTimeout(channel.retry);
    clearInterval(channel.ping);
    clearTimeout(channel.pointerTimer);
  });
  channels.clear();
  names.clear();
  privacy = { presence: true, hideCursor: false, followable: true, typingOnly: false };
  privacyLoaded = null;
}

/** This tab's own view for one source (e.g. the editor's open file), or null. */
export function getOwnView(projectId, source) {
  return channels.get(projectId)?.views.get(source) || null;
}
